import { Check, ChevronDown, Save, Users } from 'lucide-react';
import { useMemo, useState, type ChangeEvent } from 'react';
import { nextFixture, players } from '../data/seed';
import { defaultLineups, formations } from '../lib/formations';
import type { Prediction } from '../types';

interface Props {
  ownerKey: string;
  onSave: (prediction: Prediction) => Promise<void>;
  saveMode: 'cloud' | 'local';
}

export function PitchLab({ ownerKey, onSave, saveMode }: Props) {
  const [formation, setFormation] = useState('4-2-3-1');
  const [lineup, setLineup] = useState(defaultLineups['4-2-3-1']);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [firstScorer, setFirstScorer] = useState('benjamin-sesko');
  const [motm, setMotm] = useState('bruno-fernandes');
  const [homeScore, setHomeScore] = useState(2);
  const [awayScore, setAwayScore] = useState(1);
  const [saved, setSaved] = useState(false);

  const slots = formations[formation];
  const selectedPlayer = activeSlot === null ? null : players.find(player => player.id === lineup[activeSlot]);
  const used = useMemo(() => new Set(lineup), [lineup]);

  const changeFormation = (next: string) => {
    setFormation(next);
    setLineup(defaultLineups[next]);
    setActiveSlot(null);
    setSaved(false);
  };

  const replacePlayer = (playerId: string) => {
    if (activeSlot === null) return;
    const duplicateAt = lineup.findIndex(id => id === playerId);
    const next = [...lineup];
    if (duplicateAt >= 0) next[duplicateAt] = lineup[activeSlot];
    next[activeSlot] = playerId;
    setLineup(next);
    setActiveSlot(null);
    setSaved(false);
  };

  const save = async () => {
    const now = new Date().toISOString();
    const prediction: Prediction = {
      id: crypto.randomUUID(), ownerKey, fixtureId: nextFixture.id, opponent: nextFixture.opponent,
      fixtureDate: nextFixture.date, formation, startingXI: lineup, firstScorer, motm, homeScore, awayScore,
      status: 'open', createdAt: now, updatedAt: now,
    };
    await onSave(prediction);
    setSaved(true);
  };

  return (
    <section className="section-shell match-shell" id="match-lab">
      <div className="section-heading match-heading">
        <div>
          <p className="section-kicker"><Users size={15} /> Next-match prediction workspace</p>
          <h2>Build the XI from above</h2>
          <p>Choose a formation, tap a shirt to replace the player, then lock the match calls into the prediction ledger.</p>
        </div>
        <div className="fixture-chip">
          <small>{nextFixture.competition}</small>
          <strong>United v {nextFixture.opponent}</strong>
          <span>1 Aug · {nextFixture.venue}</span>
        </div>
      </div>

      <div className="match-grid">
        <div className="pitch-column">
          <div className="formation-toolbar">
            <label>Formation</label>
            <div className="select-wrap"><select value={formation} onChange={(event: ChangeEvent<HTMLSelectElement>) => changeFormation(event.target.value)}>{Object.keys(formations).map(item => <option key={item}>{item}</option>)}</select><ChevronDown size={16} /></div>
            <span>Tap any player to edit</span>
          </div>
          <div className="pitch" aria-label={`Predicted ${formation} lineup`}>
            <div className="pitch-line halfway"/><div className="pitch-circle"/><div className="pitch-box top"/><div className="pitch-box bottom"/><div className="pitch-six top"/><div className="pitch-six bottom"/>
            {slots.map((slot, index) => {
              const player = players.find(item => item.id === lineup[index]);
              return (
                <button key={slot.id} className={`player-token ${activeSlot === index ? 'active' : ''}`} style={{ left: `${slot.x}%`, top: `${slot.y}%` }} onClick={() => setActiveSlot(index)}>
                  <span className="shirt-number">{player?.number ?? '—'}</span>
                  <strong>{player?.shortName}</strong>
                  <small>{slot.label}</small>
                </button>
              );
            })}
          </div>
          {activeSlot !== null && (
            <div className="player-picker">
              <div><strong>Replace {selectedPlayer?.name}</strong><button onClick={() => setActiveSlot(null)}>Close</button></div>
              <div className="picker-list">
                {players.filter(player => player.selectable !== false && slots[activeSlot].allowed.includes(player.group)).map(player => (
                  <button key={player.id} className={used.has(player.id) ? 'used' : ''} onClick={() => replacePlayer(player.id)}>
                    <span>{player.number ?? '—'}</span><strong>{player.name}</strong><small>{player.roles.join(' / ')}</small>{used.has(player.id) && <Check size={14} />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="prediction-panel">
          <div className="scoreline-title"><span>Match calls</span><small>Maximum ledger score: 120</small></div>
          <label>First United scorer<select value={firstScorer} onChange={(event: ChangeEvent<HTMLSelectElement>) => setFirstScorer(event.target.value)}>{players.filter(player => player.selectable !== false && player.group !== 'GK').map(player => <option key={player.id} value={player.id}>{player.name}</option>)}</select></label>
          <label>Man of the match<select value={motm} onChange={(event: ChangeEvent<HTMLSelectElement>) => setMotm(event.target.value)}>{players.filter(player => player.selectable !== false).map(player => <option key={player.id} value={player.id}>{player.name}</option>)}</select></label>
          <div className="score-predictor">
            <label><span>United</span><input type="number" min="0" max="12" value={homeScore} onChange={(event: ChangeEvent<HTMLInputElement>) => setHomeScore(Number(event.target.value))}/></label>
            <b>–</b>
            <label><span>{nextFixture.opponent}</span><input type="number" min="0" max="12" value={awayScore} onChange={(event: ChangeEvent<HTMLInputElement>) => setAwayScore(Number(event.target.value))}/></label>
          </div>
          <div className="points-key">
            <span><b>5</b> each correct starter</span><span><b>5</b> formation</span><span><b>20</b> first scorer</span><span><b>20</b> MOTM</span><span><b>20</b> exact score</span>
          </div>
          <button className="primary-button" onClick={save}><Save size={18}/>{saved ? `Saved to ${saveMode === 'cloud' ? 'D1' : 'this device'}` : 'Save prediction'}</button>
          <p className="storage-note">{saveMode === 'cloud' ? 'Cloudflare D1 connected.' : 'Local fallback active until the Cloudflare D1 binding is configured.'}</p>
        </aside>
      </div>
    </section>
  );
}
